import React, { useEffect, useState, useRef } from 'react'
import Divider from '@material-ui/core/Divider'
import { AutocompleteProps as MuiAutocompleteProps } from '@material-ui/lab'
import MuiAutocomplete from '@material-ui/lab/Autocomplete'
import { TextField } from 'text-field-mui'
import Typography from 'typography'
import { ThemeProvider } from 'providers'
import styles from './styles'
import { makeStyles } from '@material-ui/core'

const useStyles = makeStyles(styles)

interface AutocompleteProps extends MuiAutocompleteProps {
    autoFocus?: boolean
    wrapInThemeProvider?: boolean
}

/**
 * Autocomplete component
 * @param {AutocompleteProps} props - props
 */
const AutocompleteBase = (props: AutocompleteProps): JSX.Element => {
    const [value, setValue] = React.useState(props.value || null)
    const [open, setOpen] = React.useState(false)
    const [loadedOptions, setLoadedOptions] = useState<string[]>([])
    const timeoutsRef = useRef<number[]>([]) // or ReturnType<typeof setTimeout>[]

    // Load options when opened in iterations of 100 in a delayed loop of 50ms
    // to avoid blocking the main thread and allow rendering of the component
    // This is useful for large datasets to prevent performance issues.
    // this is a workaround for the MUI Autocomplete component and large datasets.
    // if not open we reset the loaded options to avoid showing stale data, and clear setTimeouts
    useEffect(() => {
        // always clear any leftover timers first
        timeoutsRef.current.forEach(clearTimeout)
        timeoutsRef.current = []

        let cancelled = false
        setLoadedOptions([])

        if (!open) return

        const loadOptions = async () => {
            // eslint-disable-next-line no-unmodified-loop-condition
            for (let i = 0; i < props.options.length && !cancelled; i += 100) {
                const chunk = props.options.slice(i, i + 100)
                setLoadedOptions(prev => [...prev, ...chunk])

                await new Promise<void>(resolve => {
                    const id = window.setTimeout(() => resolve(), 50)
                    timeoutsRef.current.push(id)
                })
            }
        }

        loadOptions()

        return () => {
            // cleanup on close or dependency change
            cancelled = true
            timeoutsRef.current.forEach(clearTimeout)
            timeoutsRef.current = []
        }
    }, [open, props.options])

    const onChange = (value) => {
        setValue(value)
        props.onChange?.(value)
    }
    const { label, error, helperText, autoFocus } = props
    const classes = useStyles()

    const renderMUIAutocomplete = (): JSX.Element => (
        <MuiAutocomplete
            {...props}
            options={loadedOptions}
            classes = {{ root: classes.autocomplete, listbox: classes.listWrapper, option: 'autocomplate-list-option', ...(props.classes || {}) }}
            onChange = {(e, value) => onChange(value)}
            onOpen = {() => setOpen(true)}
            onClose = {(event, closeType) => {
                if (closeType === 'blur') setValue(value)
                setOpen(false)
            }}
            value = {open ? null : value}
            renderInput = {props.renderInput
                ? props.renderInput
                : (params) => (
                    <TextField
                        {...params}
                        wrapInThemeProvider={props.wrapInThemeProvider}
                        autoFocus={autoFocus}
                        label = {label}
                        hasFormValue = {!!value}
                        onXClick = {() => onChange(null)}
                        error = {error}
                        helperText = {helperText}
                        classNames={{ root: classes.fullWidth }}
                    />
                )}
            getOptionDisabled={option => option.divider}
            renderOption = {(option) => {
                if (option.divider) return <Divider className='divider-item'/>
                return <Typography variant='body1'>{props.getOptionLabel(option)}</Typography>
            }}
            autoComplete
        />
    )

    return props.wrapInThemeProvider
        ? (
            <ThemeProvider>
                {renderMUIAutocomplete()}
            </ThemeProvider>
        )
        : renderMUIAutocomplete()
}

AutocompleteBase.defaultProps = {
    wrapInThemeProvider: true
}

/**
 *
 */
export const Autocomplete = props => (
    <ThemeProvider>
        <AutocompleteBase {...props} />
    </ThemeProvider>
)

export default Autocomplete
